// Global API-call-rate signal.
//
// Used by background services / pollers / mutation paths to back off when
// we're approaching rate limits on any API we talk to (LinkedIn Voyager,
// Harmonic, Affinity, Supabase, webhook destinations). A single shared
// "temperature" (NONE → LOW → MEDIUM → HIGH) + a cooldown flag is enough
// for every consumer to make a decision; we don't need per-endpoint
// throttling yet.
//
// Shape inspired by the `TemperatureTracker` that already lives inside
// the extension's `messaging-engine.js`. This module brings the same
// concept to the web app so the web-side services + polling framework
// (0.9) + mutation registry (0.13) can consult a unified signal.
//
// Storage: in-memory only. Process-scoped ring buffer of recent call
// timestamps. Resets on page load. No persistence — the signal is
// relevant per-session, not across sessions.
//
// ADR: docs/adr/0021-api-temperature.md
// Related (when adopted):
//   - kondo-parity.md §5 Tier 0 item 0.8
//   - Future 0.9 polling manager (reads temperature to skip ticks)
//   - Future 0.13 mutation registry (throttle on HIGH)

import { useSyncExternalStore } from 'react'

export type ApiTemperature = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH'

/** Which API we just called. Track per-source so a noisy Harmonic
 * backfill doesn't make a quiet Supabase surface look hot. Currently
 * aggregated into a single global signal, but the event shape leaves
 * room for per-source views later. */
export type ApiSource = 'linkedin' | 'harmonic' | 'affinity' | 'supabase' | 'webhook'

// ─── Config ──────────────────────────────────────────────────────────────────

/** Observation window. We count calls inside this rolling window. */
const WINDOW_MS = 60_000

/** Call-rate thresholds. Above MEDIUM you should back off; above HIGH
 * you should stop and wait. Tuned to be conservative — for LinkedIn
 * specifically, bans kick in well below these rates in practice. */
const THRESHOLDS = {
  LOW: 10,
  MEDIUM: 50,
  HIGH: 100,
} as const

/** Cooldown after a 429 / rate-limit response. During this window
 * `isApiCooling()` returns true regardless of the count-based signal;
 * callers should respect the cooldown. */
export const API_COOLING_DURATION_MS = 60_000

// ─── State ───────────────────────────────────────────────────────────────────

/** Ring buffer of recent call timestamps. Timestamps older than
 * `WINDOW_MS` are pruned on every observation. */
const callTimestamps: number[] = []

let cooldownUntil = 0

// React-facing subscribers. Fire when the temperature changes or the
// cooldown flag flips.
const listeners = new Set<() => void>()
let lastNotifiedTemperature: ApiTemperature = 'NONE'
let lastNotifiedCoolingFlag = false

// ─── Observation API ─────────────────────────────────────────────────────────

/**
 * Record an API call. Call this from every boundary that touches an
 * external API — `supabase.rpc`, Voyager fetches, Harmonic calls, etc.
 *
 * `source` is metadata for future per-source views; the global signal
 * currently ignores it. `ok: false` + `status: 429` is what flips the
 * cooldown flag on.
 */
export function recordApiCall(source: ApiSource, opts: { ok: boolean; status?: number } = { ok: true }): void {
  void source // reserved for future per-source views
  const now = Date.now()
  callTimestamps.push(now)
  prune(now)

  if (!opts.ok && opts.status === 429) {
    cooldownUntil = Math.max(cooldownUntil, now + API_COOLING_DURATION_MS)
  }

  maybeNotify()
}

/**
 * Current rate-band. Consumers use this to decide:
 *   NONE   — full speed
 *   LOW    — slow down non-essential work
 *   MEDIUM — skip all non-essential polls
 *   HIGH   — only respond to user-initiated actions
 */
export function getApiTemperature(): ApiTemperature {
  prune(Date.now())
  const rate = callTimestamps.length
  if (rate >= THRESHOLDS.HIGH) return 'HIGH'
  if (rate >= THRESHOLDS.MEDIUM) return 'MEDIUM'
  if (rate >= THRESHOLDS.LOW) return 'LOW'
  return 'NONE'
}

/** True during the cooldown window after a 429. Background services
 * should refuse to schedule any non-user-initiated work while this is
 * true. */
export function isApiCooling(): boolean {
  return Date.now() < cooldownUntil
}

/** Reset state — tests only. Named with underscore to make the "don't
 * use this in app code" signal unambiguous. */
export function _resetApiTemperature(): void {
  callTimestamps.length = 0
  cooldownUntil = 0
  lastNotifiedTemperature = 'NONE'
  lastNotifiedCoolingFlag = false
}

// ─── Subscription ────────────────────────────────────────────────────────────
//
// Non-React consumers use `subscribeApiTemperature` directly. React
// components use the `useApiTemperature` hook below, which is a thin
// wrapper using `useSyncExternalStore`.

export function subscribeApiTemperature(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function maybeNotify(): void {
  const temperature = getApiTemperature()
  const cooling = isApiCooling()
  if (temperature === lastNotifiedTemperature && cooling === lastNotifiedCoolingFlag) return
  lastNotifiedTemperature = temperature
  lastNotifiedCoolingFlag = cooling
  for (const listener of listeners) {
    try { listener() } catch { /* never let a subscriber error break the notify loop */ }
  }
}

// ─── React hook ──────────────────────────────────────────────────────────────

/**
 * Subscribe a component to the global API temperature.
 *
 * Uses `useSyncExternalStore` for tearing-free updates across concurrent
 * renders (React 18+ concurrent mode). The snapshot includes both
 * `temperature` and `cooling` so a single render reads a consistent
 * picture.
 */
export function useApiTemperature(): { temperature: ApiTemperature; cooling: boolean } {
  return useSyncExternalStore(
    subscribeApiTemperature,
    getSnapshot,
    getServerSnapshot,
  )
}

// getSnapshot must return a stable reference when nothing has changed,
// otherwise React will complain about an infinite render loop.
let cachedSnapshot: { temperature: ApiTemperature; cooling: boolean } = { temperature: 'NONE', cooling: false }

function getSnapshot(): { temperature: ApiTemperature; cooling: boolean } {
  const temperature = getApiTemperature()
  const cooling = isApiCooling()
  if (cachedSnapshot.temperature === temperature && cachedSnapshot.cooling === cooling) {
    return cachedSnapshot
  }
  cachedSnapshot = { temperature, cooling }
  return cachedSnapshot
}

function getServerSnapshot(): { temperature: ApiTemperature; cooling: boolean } {
  // SSR-safe: always report cold. We don't have a server render path
  // today, but useSyncExternalStore requires this to be defined.
  return { temperature: 'NONE', cooling: false }
}

// ─── Internal ────────────────────────────────────────────────────────────────

function prune(now: number): void {
  const cutoff = now - WINDOW_MS
  // callTimestamps is append-only + chronologically ordered, so we can
  // find the first in-window index and splice the rest once.
  let firstInWindow = 0
  while (firstInWindow < callTimestamps.length && callTimestamps[firstInWindow] < cutoff) {
    firstInWindow++
  }
  if (firstInWindow > 0) {
    callTimestamps.splice(0, firstInWindow)
  }
}
